import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { bindActionCreators, compose } from "redux";
import _ from "lodash";
import moment from "moment";
import {
  ButtonSizeOptions,
  LabeledButtonWithCount,
  LabelString,
  PrimaryButton,
  LabelDollarFormatted,
  ModuleAccordionGroup,
  ModuleAccordionSection,
  ModuleAccordionSummary
} from "@loanbossinc/component-library";
import FolderIcon from "@material-ui/icons/Folder";
import { Fade } from "@material-ui/core";
import { CardPageLayout } from "component-lib/CardPageLayout";
import { CustomizableHeroCard } from "component-lib/CustomizableHeroCard";
import SimpleConfirmDialog from "components/Global/ConfirmationDialogs/SimpleConfirmDialog";
import UserConfirmationModal from "modals/UserConfirmationModal/UserConfirmationModal";

import LenderReportingDrawer from "../../../drawer";
import ConfigPageDisclaimer from "../../configurationPageDisclaimer";
import calcIcon from "../../../calcIcon.svg";
import { Container, LabeledIconWrapper, StickyWrapper } from "../../../dashboard/styles";
import { ListItemContent, ListItemText, ListItemTitle } from "../../styles";
import { dealGet } from "../../../../deal/deal.store";
import { actionCreators as dashboardActionCreators } from "../../../dashboard/dashboard.store";
import { actionCreators as drawerActionCreators } from "../../../drawer/lenderReportingDrawer.store";
import { actionCreators as lineItemManagementActionCreators } from "../../../drawer/line-item-management/lineItemManagement.store";
import { actionCreators as reportConfigActionCreators } from "../../../reportingSetup/reportingSetup.store";
import { actionCreators as reportComponentActionCreators } from "../../reportComponent.store";
import { actionCreators as fieldManagementActionCreators } from "../../../drawer/field-management/fieldManagement.store";

import { actionCreators as noiCalculationDashboardActionCreators } from "./noiRegularCalculationDashboard.store";
import { SummaryWrapper } from "../styles";
import { MultiplePropertiesRow, LineItemHeader } from "../dndRowComponents";
import DndContainer from "../dndContainer";
import { getTotalNoiValue, updateLineItemsIfNoiFieldIsUsed, updateSortOrder } from "../utils";

export const formattedDollarLabel = (value, fontWeight = "bold") => (
  <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <LabelDollarFormatted
      value={(value && Math.abs(value)) || " "}
      hasAccountingFormat={value < 0}
      decimals={2}
      style={{
        fontWeight,
        fontFamily: "Roboto Mono",
        color: value < 0 ? "#C40A0A" : "#000"
      }}
    />
  </div>
);

export const NoiCalculationDashboard = (props) => {
  const {
    adjustedExpenseLineItems,
    adjustedIncomeLineItems,
    deal,
    fieldCount,
    getFieldCountForDeal,
    getNoiCalcByReportComponentId,
    getSourceFileCountForDeal,
    initialDrawerSectionName,
    isAdjustedExpenseExpanded,
    isAdjustedIncomeExpanded,
    isDeleteDialogOpen,
    isLenderReportingDrawerOpen,
    lineItems,
    match: {
      params: { dealId, reportId, reportComponentId }
    },
    user,
    history,
    location,
    setNoiCalculationsDashboardSelections,
    setDefaultNoiDashboardSelections,
    putReportComponentConfig,
    postReportComponentConfig,
    sourceFileCount,
    totalAdjustedExpense,
    totalAdjustedIncome,
    totalAdjustedNoi,
    reportComponent,
    selectedReportGroup,
    setDrawerSelections,
    getReportGroupById,
    setNewLineItemSelections,
    setNewLineItemsSelectionsToDefault,
    putReportConfig,
    fieldUpdated,
    setReportComponentSelections,
    isSaving,
    wasNewReportComponentSaved,
    savedReportComponent,
    fields,
    getFields,
    setFieldSelections
  } = props;

  const [lineItemToDelete, setLineItemToDelete] = useState(null);
  const [wasContentUpdated, setWasContentUpdated] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    if (!selectedReportGroup && reportId) {
      getReportGroupById({ reportGroupId: reportId });
    }
  }, [selectedReportGroup, reportId]);

  useEffect(() => {
    if (!fields) {
      getFields({ dealId });
    }
  }, [fields]);

  useEffect(() => {
    if (_.isEmpty(deal)) {
      props.dealGet({ dealId });
    }
  }, [dealId, deal, reportId]);

  useEffect(() => {
    if (reportComponentId !== "new") {
      getNoiCalcByReportComponentId({ reportComponentId });
    }

    return () => {
      setDefaultNoiDashboardSelections();
      setReportComponentSelections({ isSaving: false, isSavingComplete: false, wasNewReportComponentSaved: false, savedReportComponent: null });
    };
  }, [reportComponentId]);

  useEffect(() => {
    if (fieldUpdated) {
      getNoiCalcByReportComponentId({ reportComponentId });
    }
  }, [fieldUpdated]);

  useEffect(() => {
    if (Boolean(dealId) || !isLenderReportingDrawerOpen) {
      getFieldCountForDeal({ dealId });
      getSourceFileCountForDeal({ dealId });
    }
  }, [dealId, isLenderReportingDrawerOpen]);

  useEffect(() => {
    if (reportComponent && selectedReportGroup && (reportComponent.dealId !== Number(dealId) || selectedReportGroup.dealId !== Number(dealId))) {
      history.push(`/deal/${dealId}/lender-reports`);
    }
  }, [dealId, reportComponent, selectedReportGroup]);

  useEffect(() => {
    if (lineItems) {
      // preserve order of line items
      const sortByOrder = (a, b) => a.sortOrder - b.sortOrder;

      const filteredItemsIncome = lineItems
        .slice()
        .filter((item) => item.type === "income")
        .sort(sortByOrder);

      const filteredItemsExpense = lineItems
        .slice()
        .filter((item) => item.type === "expense")
        .sort(sortByOrder);

      const totalIncome = getTotalNoiValue(filteredItemsIncome);
      const totalExpense = getTotalNoiValue(filteredItemsExpense);

      setNoiCalculationsDashboardSelections({
        adjustedIncomeLineItems: filteredItemsIncome,
        adjustedExpenseLineItems: filteredItemsExpense,
        totalAdjustedIncome: totalIncome,
        totalAdjustedExpense: totalExpense,
        isAdjustedExpenseExpanded: true,
        isAdjustedIncomeExpanded: true
      });
      if (selectedReportGroup) {
        /* Setting income and expense totals for the fields management drawer so a save is not required
        between adding a line item and creating a LoanBoss field using adjusted income/expense total. */
        const noiValuesPerReportGroup = [
          {
            reportGroupId: reportId,
            reportName: selectedReportGroup.reportName,
            value: totalIncome,
            type: "income"
          },
          {
            reportGroupId: reportId,
            reportName: selectedReportGroup.reportName,
            value: totalExpense,
            type: "expense"
          }
        ];
        setFieldSelections({ noiValuesPerReportGroup });
      }
    }
  }, [lineItems, fields]);

  useEffect(() => {
    setNoiCalculationsDashboardSelections({
      totalAdjustedNoi: totalAdjustedIncome - totalAdjustedExpense
    });
  }, [totalAdjustedIncome, totalAdjustedExpense]);

  useEffect(() => {
    if (wasNewReportComponentSaved) {
      setNoiCalculationsDashboardSelections({ reportComponent: savedReportComponent });
    }
  }, [wasNewReportComponentSaved, savedReportComponent]);

  // Event Handlers

  const handleRowExpansion = (group, idx) => {
    const items = group === "income" ? adjustedIncomeLineItems : adjustedExpenseLineItems;
    const updatedItems = items.map((item, index) => (idx === index ? { ...item, isExpanded: !item.isExpanded } : item));
    const combined = group === "income" ? [...updatedItems, ...adjustedExpenseLineItems] : [...adjustedIncomeLineItems, ...updatedItems];
    setNoiCalculationsDashboardSelections({ lineItems: combined });
  };

  const handleOpenLineItemDeleteDialog = (itemToDelete) => {
    setLineItemToDelete(itemToDelete);
    setNoiCalculationsDashboardSelections({ isDeleteDialogOpen: true });
  };

  const handleCloseDeleteLineItemDialog = () => {
    setNoiCalculationsDashboardSelections({ isDeleteDialogOpen: false });
  };

  const handleLineItemDelete = () => {
    const filteredLineItems = lineItems.filter((item) => !_.isEqual(item, lineItemToDelete));
    updateSortOrder(filteredLineItems);
    setWasContentUpdated(true);
    const updatedLineItems = updateLineItemsIfNoiFieldIsUsed(filteredLineItems, fields);
    setNoiCalculationsDashboardSelections({ lineItems: updatedLineItems });
    handleCloseDeleteLineItemDialog();
  };

  const handleLineItemEdit = (lineItem) => {
    const {
      field,
      lineType,
      name,
      operator,
      isMultipleProperties,
      comparisonOperator,
      comparisonCalculationType,
      multiplePropertiesValueFields,
      multiplePropertiesComparisonFields,
      comparisonSubFieldNames,
      comparisonFields
    } = lineItem;

    const editLineItemState = {
      newLineItemOperator: operator,
      selectedField: field || null,
      newLineItemName: name,
      newLineItemType: lineType,
      comparisonOperator,
      isMultipleProperties,
      comparisonCalculationType: comparisonCalculationType || null,
      multiplePropertiesValueFields: multiplePropertiesValueFields || null,
      multiplePropertiesComparisonFields: multiplePropertiesComparisonFields || null,
      multiPropComparisonSubLineItemNames: comparisonSubFieldNames || ["", ""]
    };

    // open drawer
    setDrawerSelections({
      currentSectionName: "line-item-management",
      isFromLineItemManagement: true,
      isEditingLineItem: true
    });
    setNoiCalculationsDashboardSelections({
      isLenderReportingDrawerOpen: true,
      lineItemToEdit: lineItem
    });
    // populate drawer state with line item properties
    setNewLineItemSelections({ ...editLineItemState });

    if (!isMultipleProperties && lineType === "comparison") {
      const updatedSinglePropCards = comparisonFields?.map((cf, idx) => ({
        selectedField: cf,
        name: comparisonSubFieldNames[idx] || ""
      }));
      if (updatedSinglePropCards) {
        setNewLineItemSelections({ singlePropertyComparisonCards: updatedSinglePropCards });
      }
    }

    setWasContentUpdated(true);
  };

  const handleLineItemAdd = (newLineType) => {
    setNoiCalculationsDashboardSelections({
      isLenderReportingDrawerOpen: true,
      initialDrawerSectionName: "line-item-management",
      addLineItemType: newLineType
    });
    setWasContentUpdated(true);
  };

  const closeDrawer = () => {
    setNoiCalculationsDashboardSelections({
      isLenderReportingDrawerOpen: false,
      initialDrawerSectionName: null,
      lineItemToEdit: null
    });
    setDrawerSelections({
      isFromLineItemManagement: null,
      isEditingLineItem: false
    });
    setNewLineItemsSelectionsToDefault();
  };

  const handleAdjIncomeExpansion = (e) => {
    if (e.target.innerHTML.includes("unfold_")) {
      setNoiCalculationsDashboardSelections({ isAdjustedIncomeExpanded: true });
    }
  };
  const handleAdjExpenseExpansion = (e) => {
    if (e.target.innerHTML.includes("unfold_")) {
      setNoiCalculationsDashboardSelections({
        isAdjustedExpenseExpanded: true
      });
    }
  };

  const handleOrderChange = (items, type) => {
    const itemsWithOrderUpdated = items.map((item, idx) => ({
      ...item.lineItem,
      sortOrder: idx,
      id: `${type}${idx}`
    }));

    switch (type) {
      case "income":
        setNoiCalculationsDashboardSelections({
          adjustedIncomeLineItems: itemsWithOrderUpdated,
          lineItems: [...itemsWithOrderUpdated, ...adjustedExpenseLineItems]
        });
        break;
      case "expense":
        setNoiCalculationsDashboardSelections({
          adjustedExpenseLineItems: itemsWithOrderUpdated,
          lineItems: [...adjustedIncomeLineItems, ...itemsWithOrderUpdated]
        });
        break;
      default:
        break;
    }

    setWasContentUpdated(true);
  };

  const redirectOnSaveOrCancel = () => {
    if (location.state && location.state.from === "report-wizard") {
      history.push(`/deal/${dealId}/lender-reports-setup/${reportId}`, {
        from: "undefined-4"
      });
    } else {
      history.push(`/deal/${dealId}/lender-reports/${reportId}`);
    }
  };

  const onSaveFormula = () => {
    const isEditing = reportComponentId !== "new" || wasNewReportComponentSaved;
    // omit dates in fields
    const omitDates = (line) => {
      if (!line.isMultipleProperties) {
        // single property value line item
        if (line.field) {
          return {
            ...line,
            field: _.omit(line.field, ["createdAt", "updatedAt"])
          };
        }

        // single property comparison line item
        return {
          ...line,
          comparisonFields: line.comparisonFields.map((field) => _.omit(field, ["createdAt", "updatedAt"]))
        };
      }

      const propFieldMapperFunc = (propField) => {
        const { selectedField } = propField;

        return {
          ...propField,
          selectedField: selectedField ? _.omit(selectedField, ["createdAt", "updatedAt"]) : selectedField
        };
      };

      // multiple properties value line item
      if (line.multiplePropertiesValueFields) {
        return {
          ...line,
          multiplePropertiesValueFields: line.multiplePropertiesValueFields.map(propFieldMapperFunc)
        };
      }

      // multiple properties comparison line item
      return {
        ...line,
        multiplePropertiesComparisonFields: line.multiplePropertiesComparisonFields.map((group) => group.map(propFieldMapperFunc))
      };
    };

    const incomeItemsUpdated = adjustedIncomeLineItems.map(omitDates);
    const expenseItemsUpdated = adjustedExpenseLineItems.map(omitDates);

    const combined = [...incomeItemsUpdated, ...expenseItemsUpdated];

    const combinedOmitExpanded = combined.map((li) => _.omit(li, ["isExpanded"]));

    if (isEditing) {
      const payload = {
        ...reportComponent,
        lineItems: combinedOmitExpanded
      };
      putReportComponentConfig({
        data: _.omit(payload, ["updatedAt", "createdAt"])
      });
    } else {
      const payload = {
        lineItems: combinedOmitExpanded,
        type: "NOI",
        createdBy: user?.id,
        dealId,
        reportGroupId: reportId
      };
      postReportComponentConfig({ data: payload });
    }

    getFields({ dealId: deal.id });

    putReportConfig({
      data: { id: reportId, noiLastUpdatedDate: new Date().toISOString() }
    });

    setWasContentUpdated(false);
  };

  const onCancelSaving = () => {
    redirectOnSaveOrCancel();
  };

  const onBackButtonClick = () => {
    if (wasContentUpdated) {
      setShowDialog(true);
    } else {
      redirectOnSaveOrCancel();
    }
  };

  const onConfirmDialog = () => {
    onSaveFormula();
    setShowDialog(false);
    redirectOnSaveOrCancel();
  };

  // Components

  const saveFormulaButton = {
    label: isSaving ? "Saving..." : "Save",
    onClick: onSaveFormula,
    disabled: isSaving
  };
  const cancelFormulaButton = {
    label: "Cancel changes",
    onClick: onCancelSaving
  };

  const heroCardContents = () => {
    if (!selectedReportGroup) {
      return null;
    }
    const { reportName, frequency, firstReportDueDate } = selectedReportGroup;

    return (
      <ListItemContent>
        <ListItemTitle>{reportName}</ListItemTitle>
        <ListItemText>{`Published ${frequency} and started on ${moment.utc(firstReportDueDate).format("MMMM Do, YYYY")}`}</ListItemText>
      </ListItemContent>
    );
  };

  const heroCard = () => {
    if (!deal) {
      return null;
    }

    return (
      <StickyWrapper>
        <CustomizableHeroCard
          {...props}
          buttons={[saveFormulaButton, cancelFormulaButton]}
          cardContent={heroCardContents()}
          title={deal.name}
          backLabel="All calculations"
          onBackButtonClick={onBackButtonClick}
        />

        <LabeledIconWrapper>
          <Container>
            <LabeledButtonWithCount
              label="Source Files"
              icon={<FolderIcon />}
              count={sourceFileCount}
              style={{
                width: "100%",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                backgroundColor: "#fff"
              }}
              onClick={() =>
                setNoiCalculationsDashboardSelections({
                  isLenderReportingDrawerOpen: true,
                  initialDrawerSectionName: "source-file-management"
                })
              }
            />
            <LabeledButtonWithCount
              label="Fields"
              icon={<img src={calcIcon} alt="Field Icon" height={18} width={18} />}
              count={fieldCount}
              style={{
                width: "100%",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                backgroundColor: "#fff"
              }}
              onClick={() =>
                setNoiCalculationsDashboardSelections({
                  isLenderReportingDrawerOpen: true,
                  initialDrawerSectionName: "field-management"
                })
              }
            />
          </Container>
        </LabeledIconWrapper>
      </StickyWrapper>
    );
  };

  const getRowsForGroup = (group) => {
    const groupLineItems = group === "income" ? adjustedIncomeLineItems : adjustedExpenseLineItems;
    const lineItemRows =
      groupLineItems &&
      groupLineItems.map((lineItem, idx) => {
        const isLastItem = idx === groupLineItems.length - 1;

        return {
          id: `${lineItem.sortOrder}`,
          lineItem,
          content: lineItem.isMultipleProperties ? (
            <MultiplePropertiesRow
              setNoiCalculationsDashboardSelections={setNoiCalculationsDashboardSelections}
              lineItems={lineItems}
              lineItem={lineItem}
              isLastItem={isLastItem}
              handleLineItemEdit={handleLineItemEdit}
              handleOpenLineItemDeleteDialog={handleOpenLineItemDeleteDialog}
              isExpanded={lineItem.isExpanded}
              setExpansion={() => handleRowExpansion(group, idx)}
              allFields={fields}
            />
          ) : (
            <LineItemHeader
              lineItem={lineItem}
              isLastItem={isLastItem}
              handleLineItemEdit={handleLineItemEdit}
              handleOpenLineItemDeleteDialog={handleOpenLineItemDeleteDialog}
            />
          )
        };
      });

    return lineItemRows || [];
  };

  const adjustedIncomeGroup = () => (
    <Fade in timeout={500}>
      <ModuleAccordionGroup
        onClick={handleAdjIncomeExpansion}
        shouldExpandAll={isAdjustedIncomeExpanded}
        shouldCollapseAll={!isAdjustedIncomeExpanded}
        groups={[
          {
            title: "Adjusted income",
            key: "adjust-income-group",
            isTitleGroup: false,
            isExpandable: true,
            expandButtonPosition: "right",
            sections: [
              <DndContainer items={getRowsForGroup("income")} saveOrderChange={(items) => handleOrderChange(items, "income")} />,
              <ModuleAccordionSummary
                isExpandable={false}
                title={<PrimaryButton size={ButtonSizeOptions.smFluid} label="Insert new line item" onClick={() => handleLineItemAdd("income")} />}
              />
            ]
          },
          {
            title: "Adjusted income subtotal",
            key: "adjust-income-subtotal",
            isTitleGroup: false,
            isExpandable: false,
            titleAdornmentRight: formattedDollarLabel(totalAdjustedIncome)
          }
        ]}
      />
    </Fade>
  );

  const adjustedExpenseGroup = () => (
    <Fade in timeout={500}>
      <ModuleAccordionGroup
        onClick={handleAdjExpenseExpansion}
        shouldExpandAll={isAdjustedExpenseExpanded}
        shouldCollapseAll={!isAdjustedExpenseExpanded}
        groups={[
          {
            title: "Adjusted expenses",
            key: "adjust-expenses-group",
            isTitleGroup: false,
            isExpandable: true,
            expandButtonPosition: "right",
            sections: [
              <DndContainer items={getRowsForGroup("expense")} saveOrderChange={(items) => handleOrderChange(items, "expense")} />,
              <ModuleAccordionSummary
                isExpandable={false}
                title={<PrimaryButton size={ButtonSizeOptions.smFluid} label="Insert new line item" onClick={() => handleLineItemAdd("expense")} />}
              />
            ]
          },
          {
            title: "Adjusted expenses subtotal",
            key: "adjust-expenses-subtotal",
            isTitleGroup: false,
            isExpandable: false,
            titleAdornmentRight: formattedDollarLabel(totalAdjustedExpense)
          }
        ]}
      />
    </Fade>
  );

  const summaryGroup = () => (
    <Fade in timeout={500}>
      <SummaryWrapper>
        <ModuleAccordionSection
          isExpandable={false}
          title={<LabelString value="Adjusted income" lines={1} />}
          titleAdornmentRight={formattedDollarLabel(totalAdjustedIncome, "normal")}
          style={{
            borderRadius: "8px 8px 0 0",
            backgroundColor: "#FAFAFA",
            borderBottom: "none"
          }}
        />
        <ModuleAccordionSection
          isExpandable={false}
          title={<LabelString value="Adjusted expense" lines={1} />}
          titleAdornmentRight={formattedDollarLabel(totalAdjustedExpense, "normal")}
          style={{ backgroundColor: "#FAFAFA", borderBottom: "none" }}
        />
        <ModuleAccordionSection
          isExpandable={false}
          title="Adjusted NOI"
          titleAdornmentRight={formattedDollarLabel(totalAdjustedNoi)}
          style={{ borderRadius: "0 0 8px 8px" }}
        />
      </SummaryWrapper>
    </Fade>
  );

  const body = () => (
    <div>
      <ConfigPageDisclaimer />
      {adjustedIncomeGroup()}
      {adjustedExpenseGroup()}
      {summaryGroup()}
    </div>
  );

  return (
    <>
      <CardPageLayout {...props} isReversed bannerImageUrl={deal && deal.imageLarge} cardComponent={heroCard()} bodyComponent={body()} />
      <LenderReportingDrawer
        authentication={props.authentication}
        closeDrawer={closeDrawer}
        deal={deal}
        initialDrawerSectionName={initialDrawerSectionName}
        isOpen={isLenderReportingDrawerOpen}
        wasNoiDashboardUpdated={wasContentUpdated}
      />
      <SimpleConfirmDialog
        title="Confirm Delete"
        message="Are you sure you want to delete?"
        modalOpen={isDeleteDialogOpen}
        handleNo={handleCloseDeleteLineItemDialog}
        handleYes={handleLineItemDelete}
        noText="Cancel"
        yesText="Delete"
      />
      <UserConfirmationModal
        open={showDialog}
        onClose={() => {
          setShowDialog(false);
        }}
        onCancelClick={onCancelSaving}
        onConfirmClick={onConfirmDialog}
      />
    </>
  );
};

const mapStateToProps = ({
  lenderReportList,
  lenderReportingNoiCalculation,
  lenderReportingReportComponent,
  lenderReportFieldManagement: { fields },
  newDeal: { deal },
  authentication: { user }
}) => ({
  deal,
  user,
  fields,
  ...lenderReportList,
  ...lenderReportingNoiCalculation,
  ...lenderReportingReportComponent
});
const mapDispatchToProps = dispatch => bindActionCreators(
  {
    dealGet,
    ...dashboardActionCreators,
    ...noiCalculationDashboardActionCreators,
    ...drawerActionCreators,
    ...lineItemManagementActionCreators,
    ...reportConfigActionCreators,
    ...reportComponentActionCreators,
    ...fieldManagementActionCreators
  },
  dispatch
);

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(NoiCalculationDashboard);
